/** Geofence math. Pure; used by the API (authoritative) and the worker app (live distance). */
import { UNVERIFIED_ACCURACY_M } from './constants.js';

export type LatLng = { lat: number; lng: number };
export type Office = LatLng & { radius_m: number };
/** What the browser's Geolocation API gives us. `null` = unavailable/denied. */
export type LocationInput = { lat: number; lng: number; accuracy_m: number } | null | undefined;

/** Mean Earth radius (IUGG). Haversine error is < 0.5 % which is far below GPS noise. */
export const EARTH_RADIUS_M = 6_371_008.8;

const toRad = (deg: number) => (deg * Math.PI) / 180;

export function isValidLatLng(lat: unknown, lng: unknown): lat is number {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * How trustworthy an accepted punch's location is:
 *  clean       inside the radius
 *  weak_gps    the accuracy circle still touches the radius (distance − accuracy ≤ radius)
 *  unverified  the phone could not get a usable fix (accuracy > 500 m); accepted and flagged
 */
export type LocationQuality = 'clean' | 'weak_gps' | 'unverified';

export type LocationVerdict =
  | {
      ok: true;
      quality: LocationQuality;
      distance_m: number;
      accuracy_m: number;
      weak_gps: boolean;
      unverified: boolean;
    }
  | { ok: false; reason: 'location_missing' | 'invalid_coordinates' }
  | { ok: false; reason: 'outside_geofence'; distance_m: number; accuracy_m: number };

/**
 * Location never blocks a punch; it only decides whether the punch is clean or flagged. The one
 * rejection is a fix that is good enough to trust (accuracy ≤ 500 m) and still places the phone
 * outside the radius even after allowing for its accuracy. A fix worse than 500 m cannot say
 * where the phone is, so the punch is accepted as `unverified` for the admin to review. Missing
 * or malformed coordinates are rejected because they come from a broken or tampered client, not
 * from a weak signal.
 */
export function evaluateLocation(
  loc: LocationInput,
  office: Office,
  unverifiedAccuracyM: number = UNVERIFIED_ACCURACY_M,
): LocationVerdict {
  if (!loc) return { ok: false, reason: 'location_missing' };
  if (
    !isValidLatLng(loc.lat, loc.lng) ||
    typeof loc.accuracy_m !== 'number' ||
    !Number.isFinite(loc.accuracy_m) ||
    loc.accuracy_m < 0
  ) {
    return { ok: false, reason: 'invalid_coordinates' };
  }
  const distance_m = Math.round(haversineMeters(loc, office) * 10) / 10;
  const accuracy_m = loc.accuracy_m;
  const base = { ok: true as const, distance_m, accuracy_m };
  if (accuracy_m > unverifiedAccuracyM) {
    return { ...base, quality: 'unverified', weak_gps: false, unverified: true };
  }
  if (distance_m <= office.radius_m) {
    return { ...base, quality: 'clean', weak_gps: false, unverified: false };
  }
  if (distance_m - accuracy_m <= office.radius_m) {
    return { ...base, quality: 'weak_gps', weak_gps: true, unverified: false };
  }
  return { ok: false, reason: 'outside_geofence', distance_m, accuracy_m };
}
